import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { useFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import { SeriesAvailability } from '~/common/types/series-availability';
import type { SeriesOverview } from '~/common/types/series-overview';
import { UserAvailability } from '~/common/types/user-availability';
import { routes } from '~/utils/routes';
import { useFetchSeriesUserAvailability } from '@app/_hooks/use-fetch-series-user-availability';
import { useFollowUser } from '@app/_hooks/use-follow-user';
import { useSystemNotification } from '@app/_hooks/use-system-notification';
import { navigate } from '@app/actions';
import followRequiredIcon from './assets/follow-required-icon.svg';
import mutualFollowRequiredIcon from './assets/mutual-follow-required-icon.svg';
export const useLocalHooks = (series: SeriesOverview, referer?: string) => {
  const { session } = useTellerSession();
  const [isOpen, setIsOpen] = useState(false);

  const { logUserFollow } = useFirebaseAnalytics();

  const { userAvailability } = useFetchSeriesUserAvailability(
    series.id,
    series.availability
  );

  const [isAuthorFollowed, setAuthorFollowed] = useState(
    userAvailability === UserAvailability.REQUIRE_AUTHOR_FOLLOW_USER
  );

  useEffect(() => {
    if (userAvailability) {
      setAuthorFollowed(
        userAvailability === UserAvailability.REQUIRE_AUTHOR_FOLLOW_USER
      );
    }
  }, [userAvailability]);

  const onClose = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (
      isAuthorFollowed &&
      (series.availability === SeriesAvailability.FOLLOWER ||
        userAvailability === UserAvailability.REQUIRE_NONE)
    ) {
      return;
    }

    if (!referer || new URL(window.location.href).pathname === referer) {
      navigate(routes.home.url());
    } else {
      try {
        navigate(referer);
      } catch {
        navigate(routes.home.url());
      }
    }
    setIsOpen(false);
  }, [isAuthorFollowed, referer, series.availability, userAvailability]);

  const isFollowRequired = useMemo(() => {
    return !!(
      // 投稿が自分自身ではない
      (
        series.user?.id &&
        session?.tellerUser?.id !== series.user?.id &&
        // ユーザの表示条件でフォローなどする必要があるか
        userAvailability &&
        userAvailability !== UserAvailability.REQUIRE_NONE
      )
    );
  }, [series.user?.id, session?.tellerUser?.id, userAvailability]);

  useEffect(() => {
    if (isFollowRequired && series.user && userAvailability) {
      setIsOpen(true);
    }
  }, [isFollowRequired, series.user, userAvailability]);

  const title = useMemo(() => {
    if (series.availability === SeriesAvailability.FOLLOWER) {
      return '「フォロワー限定」';
    }
    return '「相互フォロー限定」';
  }, [series]);

  const icon = useMemo(() => {
    if (series.availability === SeriesAvailability.FOLLOWER) {
      return followRequiredIcon;
    }
    return mutualFollowRequiredIcon;
  }, [series]);

  const note = useMemo(() => {
    if (series.availability === SeriesAvailability.FOLLOWER) {
      return '作品の作者をフォローしている人だけが見ることができます。';
    }
    return '作品の作者と相互フォローし合っている人だけが見ることができます。';
  }, [series]);

  const buttonText = useMemo(() => {
    if (session?.isAnonymous) {
      return '新規登録';
    }
    if (
      isAuthorFollowed &&
      series.availability === SeriesAvailability.MUTUAL_FOLLOWER
    ) {
      return 'フォローバック待ち';
    }
    if (isAuthorFollowed) {
      return 'すでにフォロー中です';
    }
    switch (userAvailability) {
      case UserAvailability.REQUIRE_USER_FOLLOW_AUTHOR:
        return '作者をフォローして読む';
      case UserAvailability.REQUIRE_AUTHOR_FOLLOW_USER:
        return 'すでにフォロー中です';
      default:
        return '作者をフォローする';
    }
  }, [
    isAuthorFollowed,
    series.availability,
    session?.isAnonymous,
    userAvailability,
  ]);

  const { followUser } = useFollowUser();
  const eventPage = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.pathname;
  }, []);
  const eventParentPage = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return location.pathname.replace(/\/[^/]*$/, '');
  }, []);

  const { notify } = useSystemNotification();

  const followButtonClick = useCallback(async () => {
    if (session?.isAnonymous) {
      navigate(routes.register.url());
      return;
    }
    try {
      if (!series.user) return;
      const seriesId =
        userAvailability === UserAvailability.REQUIRE_MUTUAL_FOLLOW
          ? series.id
          : undefined;
      await followUser(series.user.id, seriesId);
      setAuthorFollowed(true);
      logUserFollow(
        eventPage,
        eventParentPage,
        series.user.id,
        series.availability === SeriesAvailability.FOLLOWER
          ? 'follower_only_access_modal'
          : 'mutual_only_access_modal'
      );
      if (userAvailability === UserAvailability.REQUIRE_USER_FOLLOW_AUTHOR) {
        setIsOpen(false);
      }
    } catch {
      notify(
        'フォローに失敗しました。時間をおいて再度お試しください。',
        'error'
      );
    }
  }, [
    eventPage,
    eventParentPage,
    followUser,
    logUserFollow,
    notify,
    series.availability,
    series.id,
    series.user,
    session?.isAnonymous,
    userAvailability,
  ]);

  return {
    isOpen,
    onClose,
    title,
    icon,
    note,
    buttonText,
    followButtonClick,
    isAuthorFollowed,
    userAvailability,
    reportLink: routes.series.copyrightReport.image.url(series.id),
  };
};
